import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, RotateCcw, Percent, Save, Ban, Plus, Trash2, X, Unlink, FileText, AlertTriangle } from 'lucide-react'
import { getReturnConfig, saveReturnConfig, getReturnLabels, voidReturnLabel, createReturnLabel, deleteReturnLabel, unpairEasyshipLabel, openReturnLabelPdf } from '../lib/api'
import { useToast, ToastContainer } from './Toast'
import { businessToday } from '../lib/dates'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}

const currentMonthStart = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

// A FACTORY, not a constant. As a module-level object the date was frozen at
// bundle-evaluation time, so a tab left open past midnight kept offering
// yesterday. React invokes a function initialiser lazily, so useState(EMPTY_FORM)
// below now recomputes it each time the modal mounts.
const EMPTY_FORM = () => ({
  order_number: '',
  tracking_number: '',
  carrier_code: '',
  service_code: '',
  carrier_cost: '',
  label_created_at: businessToday(),
})

function AddLabelModal({ onClose, onSave, isPending }) {
  const [form, setForm] = useState(EMPTY_FORM)

  function handleChange(key, value) {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.label_created_at) return
    onSave({
      order_number:     form.order_number.trim()     || null,
      tracking_number:  form.tracking_number.trim()  || null,
      carrier_code:     form.carrier_code.trim()     || null,
      service_code:     form.service_code.trim()     || null,
      carrier_cost:     parseFloat(form.carrier_cost) || 0,
      label_created_at: form.label_created_at,
    })
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-800">Add Return Label</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <X size={18} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4">
              {/* Date — required */}
              <div>
                <label className="label">Date <span className="text-red-400">*</span></label>
                <input
                  type="date"
                  required
                  value={form.label_created_at}
                  onChange={e => handleChange('label_created_at', e.target.value)}
                  className="input"
                />
              </div>

              {/* Order # and Tracking side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Order #</label>
                  <input
                    type="text"
                    value={form.order_number}
                    onChange={e => handleChange('order_number', e.target.value)}
                    className="input"
                    placeholder="e.g. 10042"
                  />
                </div>
                <div>
                  <label className="label">Tracking #</label>
                  <input
                    type="text"
                    value={form.tracking_number}
                    onChange={e => handleChange('tracking_number', e.target.value)}
                    className="input"
                    placeholder="e.g. 7E1234..."
                  />
                </div>
              </div>

              {/* Carrier and Service side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Carrier</label>
                  <input
                    type="text"
                    value={form.carrier_code}
                    onChange={e => handleChange('carrier_code', e.target.value)}
                    className="input"
                    placeholder="e.g. auspost"
                  />
                </div>
                <div>
                  <label className="label">Service</label>
                  <input
                    type="text"
                    value={form.service_code}
                    onChange={e => handleChange('service_code', e.target.value)}
                    className="input"
                    placeholder="e.g. parcel_post"
                  />
                </div>
              </div>

              {/* Carrier Cost */}
              <div>
                <label className="label">Carrier Cost ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.carrier_cost}
                  onChange={e => handleChange('carrier_cost', e.target.value)}
                  className="input"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
              <button type="button" onClick={onClose} className="btn-secondary" disabled={isPending}>
                Cancel
              </button>
              <button type="submit" disabled={isPending} className="btn-primary">
                {isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {isPending ? 'Saving…' : 'Add Label'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default function ReturnsTab({ clientId }) {
  const [fromDate, setFromDate] = useState(currentMonthStart())
  const [toDate, setToDate]     = useState('')
  const [marginInput, setMarginInput] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const { toasts, addToast, removeToast } = useToast()
  const queryClient = useQueryClient()

  const params = {
    ...(fromDate && { from: fromDate }),
    ...(toDate   && { to:   toDate   }),
  }

  const { data: config, isLoading: configLoading, error: configError } = useQuery({
    queryKey: ['return-config', clientId],
    queryFn:  () => getReturnConfig(clientId),
    enabled:  !!clientId,
  })

  if (config && marginInput === null && !configLoading) {
    setMarginInput(String(((config.margin_pct ?? 0) * 100).toFixed(1)))
  }

  const { data: labels = [], isLoading: labelsLoading, error: labelsError } = useQuery({
    queryKey: ['return-labels', clientId, params],
    queryFn:  () => getReturnLabels(clientId, params),
    enabled:  !!clientId,
  })

  const configMutation = useMutation({
    mutationFn: (pct) => saveReturnConfig(clientId, { margin_pct: pct / 100 }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['return-config', clientId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      addToast({ type: 'success', message: 'Margin saved.' })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Save failed.' }),
  })

  const voidMutation = useMutation({
    mutationFn: ({ labelId, voided }) => voidReturnLabel(labelId, voided),
    onSuccess: (_, { voided }) => {
      queryClient.invalidateQueries({ queryKey: ['return-labels', clientId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      addToast({ type: 'success', message: voided ? 'Return label voided.' : 'Return label restored.' })
    },
    onError: () => addToast({ type: 'error', message: 'Failed to update return label.' }),
  })

  const createMutation = useMutation({
    mutationFn: (data) => createReturnLabel(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['return-labels', clientId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      setShowAddModal(false)
      addToast({ type: 'success', message: 'Return label added.' })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to add return label.' }),
  })

  const deleteMutation = useMutation({
    mutationFn: (labelId) => deleteReturnLabel(labelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['return-labels', clientId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      addToast({ type: 'success', message: 'Return label deleted.' })
    },
    onError: () => addToast({ type: 'error', message: 'Failed to delete return label.' }),
  })

  const unpairMutation = useMutation({
    mutationFn: (labelId) => unpairEasyshipLabel(labelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['return-labels', clientId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      queryClient.invalidateQueries({ queryKey: ['easyship'] })
      addToast({ type: 'success', message: 'Easyship label unmatched — it will reappear in Unmatched Easyship.' })
    },
    onError: () => addToast({ type: 'error', message: 'Failed to unpair Easyship label.' }),
  })

  function handleVoid(label) {
    if (!label.voided && !window.confirm(
      `Void return label for order ${label.order_number || label.id}?\n\nCosts will show as $0 and it will be excluded from billing. Click the icon again to restore it.`
    )) return
    voidMutation.mutate({ labelId: label.id, voided: !label.voided })
  }

  function handleDelete(label) {
    if (!window.confirm(`Delete this manually entered return label?\n\nOrder: ${label.order_number || '—'}\nTracking: ${label.tracking_number || '—'}\n\nThis cannot be undone.`)) return
    deleteMutation.mutate(label.id)
  }

  function handleUnpair(label) {
    if (!window.confirm(`Unpair this Easyship label?\n\nOrder: ${label.order_number || '—'}\n\nIt will return to the Unmatched Easyship table for rematching.`)) return
    unpairMutation.mutate(label.id)
  }

  const pdfMutation = useMutation({
    mutationFn: (labelId) => openReturnLabelPdf(labelId),
    onError: (err) => addToast({
      type: 'error',
      message: err?.response?.data?.detail || 'Failed to fetch return label PDF.',
    }),
  })

  const marginPct          = config?.margin_pct ?? 0
  const flatReturnRate     = config?.flat_return_rate ?? 0
  const exceptionThreshold = config?.shipping_exception_threshold ?? 0
  const displayMargin = marginInput !== null ? marginInput : String((marginPct * 100).toFixed(1))

  function computeCharged(label) {
    const carrierCost = label.carrier_cost || 0
    // Manual and Easyship entries always use carrier cost × margin:
    // - Manual: user entered the raw cost, flat rate shouldn't override it
    // - Easyship: carrier_cost already includes the 1.7% + $2 OPS base fee
    const bypassFlat = label.manual || label.source === 'easyship'
    if (!bypassFlat && flatReturnRate > 0) {
      const isException = exceptionThreshold > 0 && carrierCost > exceptionThreshold
      return isException ? carrierCost * (1 + marginPct) : flatReturnRate
    }
    return carrierCost * (1 + marginPct)
  }

  const activeLabels = labels.filter(l => !l.voided)
  const totalCost    = activeLabels.reduce((s, l) => s + (l.carrier_cost || 0), 0)
  const totalCharged = activeLabels.reduce((s, l) => s + computeCharged(l), 0)

  // Show delete column if any label is manual; unpair column if any are easyship
  const hasManual   = labels.some(l => l.manual)
  const hasEasyship = labels.some(l => l.source === 'easyship')

  return (
    <div className="space-y-4">
      {/* Config card */}
      <div className="card p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Return Label Margin</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {flatReturnRate > 0
                ? `Flat rate: $${flatReturnRate.toFixed(2)}/label${exceptionThreshold > 0 ? ` (margin applies when carrier cost > $${exceptionThreshold.toFixed(2)})` : ''}`
                : 'Applied on top of the carrier cost. Charged = cost × (1 + margin%).'}
            </p>
            {/* A failed config fetch leaves `config` undefined, so every `?? 0`
                below reads as a genuine 0% margin / no flat rate and the Charged
                column quietly bills at bare cost. Say so instead. */}
            {configError && (
              <p className="text-xs text-red-600 mt-1.5 flex items-start gap-1">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                <span>
                  Margin config failed to load — this is <strong>not</strong> a 0% margin, and the
                  Charged figures below are understated until it loads.{' '}
                  <span className="text-slate-400 font-mono">
                    {configError?.response?.data?.detail || configError?.message || 'Unknown error'}
                  </span>
                </span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="number"
                min="0"
                max="200"
                step="0.1"
                value={displayMargin}
                onChange={e => setMarginInput(e.target.value)}
                className="input text-sm w-28 pr-7"
                placeholder="0.0"
              />
              <Percent size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <button
              onClick={() => configMutation.mutate(parseFloat(displayMargin) || 0)}
              disabled={configMutation.isPending || configLoading}
              className="btn-primary text-xs py-1.5 px-3"
            >
              {configMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Labels table */}
      <div className="card overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">Return Labels</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <span className="text-slate-400 text-xs">to</span>
            <input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary text-xs py-1.5 px-3"
            >
              <Plus size={13} />
              Add Label
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tracking</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Carrier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Service</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Carrier Cost</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Charged</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Void</th>
                {hasManual && (
                  <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Del</th>
                )}
                {hasEasyship && (
                  <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Unpair</th>
                )}
                <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">PDF</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {labelsLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 9 + (hasManual ? 1 : 0) + (hasEasyship ? 1 : 0) }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="skeleton h-4 rounded" /></td>
                    ))}
                  </tr>
                ))
              ) : labelsError ? (
                /* `labels = []` turns a failed fetch into the "no return labels"
                   state below — and an empty period reads as nothing to bill. */
                <tr>
                  <td colSpan={9 + (hasManual ? 1 : 0) + (hasEasyship ? 1 : 0)} className="py-12 text-center">
                    <AlertTriangle size={28} className="mx-auto text-red-400 mb-2" />
                    <p className="text-red-600 text-sm font-medium">Failed to load return labels</p>
                    <p className="text-red-500 text-xs mt-1">
                      This is a load failure, <strong>not</strong> an empty period — labels may exist for
                      this range, so treat the missing totals as unknown, not zero.
                    </p>
                    <p className="text-slate-400 text-xs mt-1 font-mono">
                      {labelsError?.response?.data?.detail || labelsError?.message || 'Unknown error'}
                    </p>
                  </td>
                </tr>
              ) : labels.length === 0 ? (
                <tr>
                  <td colSpan={9 + (hasManual ? 1 : 0) + (hasEasyship ? 1 : 0)} className="py-12 text-center">
                    <RotateCcw size={28} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-400 text-sm">No return labels in this period</p>
                    <p className="text-slate-300 text-xs mt-1">Run a Full Sync on the Orders tab to pull return labels from ShipStation, or add one manually above</p>
                  </td>
                </tr>
              ) : (
                <>
                  {labels.map(label => {
                    const charged     = label.voided ? 0 : computeCharged(label)
                    const displayCost = label.voided ? 0 : (label.carrier_cost || 0)
                    return (
                      <tr key={label.id} className={`transition-colors ${label.voided ? 'bg-red-50 opacity-60' : label.manual ? 'bg-blue-50/40 hover:bg-blue-50' : label.source === 'easyship' ? 'bg-purple-50/30 hover:bg-purple-50/60' : 'hover:bg-slate-50'}`}>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700">
                          {label.order_number || '—'}
                          {label.manual && (
                            <span className="ml-1.5 text-xs font-medium bg-blue-100 text-blue-600 rounded px-1 py-0.5 align-middle">manual</span>
                          )}
                          {label.source === 'easyship' && (
                            <span className="ml-1.5 text-xs font-medium bg-purple-100 text-purple-600 rounded px-1 py-0.5 align-middle">easyship</span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">
                          {label.tracking_number || '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">
                          {label.carrier_code || '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">
                          {label.service_code || '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {label.label_created_at}
                        </td>
                        <td className="px-3 py-3 text-right text-xs">
                          <span className={label.voided ? 'text-red-400 line-through' : 'text-slate-500'}>
                            {formatCurrency(displayCost)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <span className={label.voided ? 'text-red-400 line-through' : 'font-semibold text-slate-800'}>
                            {formatCurrency(charged)}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button
                            onClick={() => handleVoid(label)}
                            disabled={voidMutation.isPending}
                            title={label.voided ? 'Restore this return label' : 'Void this return label (remove from billing)'}
                            className={`p-1 rounded transition-colors ${label.voided ? 'text-red-500 hover:text-red-700' : 'text-slate-200 hover:text-slate-400'}`}
                          >
                            {voidMutation.isPending && voidMutation.variables?.labelId === label.id
                              ? <Loader2 size={14} className="animate-spin" />
                              : <Ban size={14} />}
                          </button>
                        </td>
                        {hasManual && (
                          <td className="px-3 py-3 text-center">
                            {label.manual ? (
                              <button
                                onClick={() => handleDelete(label)}
                                disabled={deleteMutation.isPending}
                                title="Delete this manually entered label"
                                className="p-1 rounded transition-colors text-slate-200 hover:text-red-500"
                              >
                                {deleteMutation.isPending && deleteMutation.variables === label.id
                                  ? <Loader2 size={14} className="animate-spin" />
                                  : <Trash2 size={14} />}
                              </button>
                            ) : null}
                          </td>
                        )}
                        {hasEasyship && (
                          <td className="px-3 py-3 text-center">
                            {label.source === 'easyship' ? (
                              <button
                                onClick={() => handleUnpair(label)}
                                disabled={unpairMutation.isPending}
                                title="Unpair — return to Unmatched Easyship for rematching"
                                className="p-1 rounded transition-colors text-slate-200 hover:text-purple-500"
                              >
                                {unpairMutation.isPending && unpairMutation.variables === label.id
                                  ? <Loader2 size={14} className="animate-spin" />
                                  : <Unlink size={14} />}
                              </button>
                            ) : null}
                          </td>
                        )}
                        <td className="px-3 py-3 text-center">
                          {label.source !== 'easyship' && !label.manual && label.tracking_number ? (
                            <button
                              onClick={() => pdfMutation.mutate(label.id)}
                              disabled={pdfMutation.isPending}
                              title="Open ShipStation label PDF in a new tab"
                              className="p-1 rounded transition-colors text-slate-300 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                            >
                              {pdfMutation.isPending && pdfMutation.variables === label.id
                                ? <Loader2 size={14} className="animate-spin" />
                                : <FileText size={14} />}
                            </button>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}

                  {/* Totals row */}
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td colSpan={5} className="px-4 py-3 text-right text-xs font-semibold text-slate-500">
                      Total ({activeLabels.length} label{activeLabels.length !== 1 ? 's' : ''}{labels.length !== activeLabels.length ? `, ${labels.length - activeLabels.length} voided` : ''})
                    </td>
                    <td className="px-3 py-3 text-right text-xs font-medium text-slate-500">
                      {formatCurrency(totalCost)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-slate-800 text-sm">
                      {formatCurrency(totalCharged)}
                    </td>
                    <td />
                    {hasManual && <td />}
                    {hasEasyship && <td />}
                    <td />
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <AddLabelModal
          onClose={() => setShowAddModal(false)}
          onSave={data => createMutation.mutate(data)}
          isPending={createMutation.isPending}
        />
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}
